"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { useCallback, useEffect, useState } from "react"
import { lactoseFlagLevel } from "@/lib/constraintClarification"
import type { LactoseLevel } from "@bitebalance/contracts"
import { Link, useParams } from "react-router-dom"
import type {
  DayLedgerResponse,
  HardConstraintsResponse,
  MealInstance,
  PlanResponse,
  RecipeAllergenEntry,
  RecipeDetailResponse,
  RecipeProvenance,
} from "@bitebalance/contracts"
import { ApiError, isDeadPlanPreview } from "@bitebalance/contracts/client"
import { useApiClient } from "@/api/useApiClient"
import {
  applyFailed,
  applyStarted,
  applySucceeded,
  canApply,
  cancelPreview,
  initialPortionPreview,
  previewArrived,
  previewFailed,
  stepServings,
  type PortionPreviewData,
  type PortionPreviewRequest,
  type PortionPreviewState,
} from "@/lib/portionPreview"
import {
  useI18n,
  formatKcal,
  formatMinutes,
  formatServings,
  type I18nContextValue,
  type Locale,
} from "@/i18n"
import { ingredientQuantityText } from "@/lib/ingredientQuantity"
import { LoadingState, EmptyState, ErrorState } from "@/components/state"
import { humanizeRecipeId, reasonBadgeLabel, slotLabel } from "@/components/plan/recipeDisplay"
import { ServingsStepper, servingsLabel } from "@/components/recipe/ServingsStepper"
import {
  RecipeIngredientsPanel,
  RecipeAllergenPanel,
  RecipeStepsPanel,
} from "@/components/recipe/RecipeGapPanels"
import { OutcomeSheet } from "@/components/plan/OutcomeSheet"
import { SwapProposalSheet } from "@/components/plan/SwapProposalSheet"
import { notifyAmyDataChanged } from "@/lib/amySync"
import { Button } from "@/components/ui/button"

type LoadStatus = "loading" | "success" | "error"
type TFn = I18nContextValue["t"]

/** Mechanical readable form of a server enum value (e.g. "may_contain" -> "May contain") — real data, never invented. Mirrors mobile's local humanizeEnum. */
function humanizeEnum(value: string): string {
  return humanizeRecipeId(value)
}

/**
 * Allergen relation label. "unknown" is a fail-closed aggregate (ADR-012 §1)
 * — it must read as a risk, not as "no data", so it gets an explicit,
 * locale-aware label rather than the mechanical enum transform used for
 * "contains"/"may_contain".
 */
function allergenRelationLabel(relation: RecipeAllergenEntry["relation"], locale: Locale): string {
  if (relation === "unknown") {
    return locale === "de" ? "Unbekannt — wird als Risiko behandelt" : "Unknown — treated as risk"
  }
  return humanizeEnum(relation)
}

function provenanceLabel(provenance: RecipeProvenance, t: TFn): string {
  if (provenance === "amy_generated") return t("views.recipe.provenanceAmy")
  return humanizeEnum(provenance)
}

/**
 * Recipe detail (T12/S-5), reached from a plan meal card's "Open recipe"
 * action at /app/recipes/{mealId}. Fetches the current plan and looks up
 * the meal instance by id so the route survives a page reload — see
 * MealInstance's fields for what's authoritative (nutrition/time/cost) —
 * then loads GET /v1/recipes/{recipeId} for sourced ingredients, allergens
 * with relation + provenance, steps, and per-serving nutrition (port of
 * mobile/src/app/recipe/[mealId].tsx's data flow).
 */
export default function RecipePage() {
  const { mealId } = useParams<{ mealId: string }>()
  const api = useApiClient()
  const { t, locale } = useI18n()
  const [status, setStatus] = useState<LoadStatus>("loading")
  const [plan, setPlan] = useState<PlanResponse | null>(null)
  // Portion preview truth (ADR-055): ONE state machine per recipe surface
  // drives the stepper, the preview card AND the ingredient section. It is
  // (re)seeded from the plan's authoritative serving on every load/reload —
  // an un-applied preview never survives a reload.
  const [portion, setPortion] = useState<PortionPreviewState | null>(null)
  // Release-readiness P1 (founder-reported, 2026-09-19): the recipe page
  // could not show or change this meal's own outcome. `plan.meals` (above)
  // has no outcome field — it is plan INTENT, not a consumption record — so
  // this is a SECOND, independent, non-blocking fetch of the day ledger for
  // this meal's own date (the exact call TodayPage already makes). The
  // write reuses the SAME OutcomeSheet/api.recordMealOutcome TodayPage and
  // WeekGrid already share — never a second mutation path.
  const [ledger, setLedger] = useState<DayLedgerResponse | null>(null)
  const [outcomeMeal, setOutcomeMeal] = useState<MealInstance | null>(null)
  // "Swap dish" (launch fix 2026-10-13): the SAME SwapProposalSheet Today's meal card and the week grid open — the
  // candidate list, the pinned review, the atomic apply. This page only hosts it; it is not a second flow.
  const [swapMeal, setSwapMeal] = useState<MealInstance | null>(null)

  const load = useCallback(() => {
    setStatus("loading")
    api
      .getCurrentPlan()
      .then(result => {
        const planned = result.meals.find(m => m.id === mealId)
        setPlan(result)
        setPortion(planned ? initialPortionPreview(planned.servings) : null)
        setStatus("success")
      })
      .catch(() => setStatus("error"))
  }, [api, mealId])

  useEffect(() => {
    load()
  }, [load])

  // Hooks run unconditionally (before the early returns below), reading the
  // meal's date from `plan` directly rather than the narrowed `meal`/
  // `currentMeal` bindings those returns introduce.
  const mealDate = plan?.meals.find(m => m.id === mealId)?.date
  const loadLedger = useCallback(() => {
    if (!mealDate) return Promise.resolve()
    // Returns the chained promise (existing fire-and-forget callers simply
    // never await it) so submitHad below can wait for the reload to
    // actually finish before clearing its own busy state.
    return api
      .getDayLedger(mealDate)
      .then(setLedger)
      .catch(() => {
        /* honest gap: the outcome row simply stays hidden, never a guess */
      })
  }, [api, mealDate])

  useEffect(() => {
    loadLedger()
  }, [loadLedger])

  // Release-readiness P1, item B (2026-09-21): "Had it" writes directly —
  // the SAME api.recordMealOutcome call OutcomeSheet's own full-portion log
  // makes, never a second mutation path — instead of opening the sheet.
  // Declared here (using `mealId`, not `currentMeal.id`) so this hook stays
  // unconditional, above the early returns below (see the comment above
  // `loadLedger` for why `mealDate`/`mealId` are used the same way).
  const [hadBusy, setHadBusy] = useState(false)
  const submitHad = useCallback(() => {
    if (!mealId) return
    setHadBusy(true)
    api
      .recordMealOutcome(mealId, { outcome: "had" })
      .then(() => loadLedger())
      .finally(() => setHadBusy(false))
  }, [api, mealId, loadLedger])

  if (status === "loading") return <LoadingState />
  if (status === "error" || !plan) return <ErrorState onRetry={load} />

  const meal = plan.meals.find(m => m.id === mealId)
  if (!meal || !portion) {
    return (
      <EmptyState
        title={t("views.recipe.notFoundTitle")}
        body={t("views.recipe.notFoundBody")}
        action={
          <Link to="/app/plan" className="text-sm font-medium text-green underline">
            {t("views.recipe.back")}
          </Link>
        }
      />
    )
  }

  const title = humanizeRecipeId(meal.recipeId)
  // Narrowed bindings for the handlers below (TS does not carry the early-
  // return narrowing into nested functions).
  const currentMeal: MealInstance = meal
  const portionState: PortionPreviewState = portion
  const planId = plan.id
  const baseRevision = plan.revision

  // Recipe's own outcome distinction (2026-09-21, architecture-review
  // finding + founder correction): the invariant is "a wrong recipe
  // identity must never mutate the slot," never "a changed outcome can
  // never be corrected." A "changed" outcome WITH a real linked
  // replacement means THIS recipe is the obsolete original — something
  // else was eaten instead — so no outcome control is offered against that
  // identity, only the truthful replacement state (mirrors the
  // fix/mobile-bugs rule MealCard already applies). A "changed" outcome
  // with NO link (a legacy/manual mark — no identity mismatch at all) stays
  // fully correctable, exactly like had/partial/skipped. ADR-056: joined by
  // SLOT, never by wire meal id — `ledger` and `plan` are fetched
  // independently and could in principle disagree on ids across a rebuild;
  // `getDayLedger(mealDate)` already scopes the date half of the key.
  const plannedMeal = ledger?.plannedMeals.find(m => m.slot === currentMeal.slot) ?? null
  const outcome = plannedMeal?.outcome ?? null
  const isObsoleteReplaced = outcome === "changed" && (plannedMeal?.replacedBy?.length ?? 0) > 0
  const outcomeStatusLabel = outcome ? t(`views.outcomeSheet.${outcome}`) : null

  /**
   * Preview for a selection: the mutation owner's `scale_servings` proposal
   * (kcal/cost/token) and the portion owner's server-scaled recipe
   * (`?servings=`) are fetched together and handed to the reducer WITH the
   * request's sequence number — the reducer alone decides whether the
   * response still answers the latest selection (out-of-order responses are
   * dropped, never rendered). Nothing here mutates the plan.
   */
  function requestPreview(request: PortionPreviewRequest) {
    const { seq, servings } = request
    Promise.all([
      api.createPlanProposal(planId, {
        baseRevision,
        scope: { mealIds: [currentMeal.id] },
        intent: "scale_servings",
        servings,
      }),
      api.getRecipe(currentMeal.recipeId, { servings }),
    ])
      .then(([proposal, recipe]) => {
        const change = proposal.changes.find(c => c.mealId === currentMeal.id)
        const scaled = recipe.portion
        if (!scaled || !change?.after) {
          // No server-scaled portion or no change for this meal: not a
          // preview we can truthfully show.
          setPortion(s => s && previewFailed(s, seq))
          return
        }
        const data: PortionPreviewData = { servings, proposal, portion: scaled }
        setPortion(s => s && previewArrived(s, seq, data))
      })
      .catch(() => setPortion(s => s && previewFailed(s, seq)))
  }

  function handleStep(direction: 1 | -1) {
    const { state: next, request } = stepServings(portionState, direction)
    setPortion(next)
    if (request) requestPreview(request)
  }

  /** Apply through the SAME confirmation flow as every plan change (preview → explicit approval → apply). */
  async function handleApply() {
    if (!canApply(portionState) || !portionState.preview) return
    const { proposal, servings: previewServings } = portionState.preview
    const confirmationToken = proposal.confirmationToken
    if (!confirmationToken) return
    setPortion(s => s && applyStarted(s))
    try {
      const result = await api.applyPlanProposal(planId, proposal.proposalId, {
        confirmationToken,
        baseRevision: proposal.baseRevision,
      })
      const applied =
        result.plan.meals.find(m => m.id === currentMeal.id)?.servings ?? previewServings
      // The persisted revision is the new truth: header, meal.servings and
      // the planned-portion fetch all follow it without a full reload.
      setPlan(result.plan)
      setPortion(s => s && applySucceeded(s, applied))
    } catch (err) {
      const kind =
        err instanceof ApiError && err.envelope.code === "STALE_REVISION"
          ? "stale"
          : isDeadPlanPreview(err)
            ? // F14: superseded / consumed / expired token — the held preview is dead; "stale" drops it and re-reads the plan
              "stale"
            : err instanceof ApiError && err.envelope.code === "MAX_CHECKOUT_CONFLICT"
              ? "max_conflict"
              : "apply"
      setPortion(s => s && applyFailed(s, kind))
    }
  }

  function handleCancel() {
    setPortion(s => s && cancelPreview(s))
  }

  return (
    <section
      aria-labelledby="recipe-heading"
      className="mx-auto flex w-full max-w-2xl flex-col gap-6"
    >
      <Link to="/app/plan" className="text-sm text-ink-2 underline">
        {t("views.recipe.back")}
      </Link>

      <div>
        <p className="text-xs font-medium uppercase tracking-wide text-ink-2">
          {slotLabel(meal.slot, t)}
        </p>
        <h1 id="recipe-heading" className="font-display text-3xl font-bold tracking-tight text-ink">
          {title}
        </h1>
        {meal.reasonCodes.length > 0 && (
          <ul className="mt-2 flex flex-wrap gap-1.5">
            {meal.reasonCodes.map(code => (
              <li
                key={code}
                className="rounded-bb-full border border-line bg-cream px-2 py-0.5 text-xs text-ink-2"
              >
                {reasonBadgeLabel(code, t)}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Release-readiness P1, item B (2026-09-21): the same status-Pill-left /
          established-Button-right grammar the mobile Recipe screen and
          MealCard's own outcome pill already use — no control at all against
          an obsolete, replaced recipe identity (only the truthful note);
          "Had it" writes directly (the same api.recordMealOutcome OutcomeSheet's
          own full-portion log makes); "Other outcome"/"Change" open the SAME
          OutcomeSheet TodayPage/WeekGrid already use. No new copy. */}
      {isObsoleteReplaced ? (
        <p className="text-sm font-medium text-green">{t("views.mealCard.changedNote")}</p>
      ) : outcome ? (
        <div className="flex min-h-9 items-center justify-between gap-2">
          <span
            className={`inline-flex items-center gap-1 rounded-bb-full px-2.5 py-1 text-xs font-semibold ${
              outcome === "skipped" || outcome === "changed"
                ? "bg-cream text-ink-2"
                : "bg-green/10 text-green"
            }`}
          >
            {outcomeStatusLabel}
          </span>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setOutcomeMeal(currentMeal)}
          >
            {t("views.mealCard.changeOutcome")}
          </Button>
        </div>
      ) : (
        <div className="flex min-h-9 items-center gap-2">
          <Button type="button" size="sm" variant="default" disabled={hadBusy} onClick={submitHad}>
            {t("views.mealCard.had")}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setOutcomeMeal(currentMeal)}
          >
            {t("views.mealCard.other")}
          </Button>
        </div>
      )}

      {/* A dish that has not been logged can be swapped from here, not only from a plan card. A batch dinner gets the
          sheet's honest "no other dish fits" card (server rule, PL-25) — no special case on the client. */}
      {!isObsoleteReplaced && !outcome ? (
        <div className="-mt-3">
          <Button
            type="button"
            size="sm"
            variant="outline"
            data-testid="swap-dish-button"
            onClick={() => setSwapMeal(currentMeal)}
          >
            {t("views.mealCard.swapDish")}
          </Button>
        </div>
      ) : null}

      {/* Portion truth (2026-09-04): ONE portion story. The header is the
          user's PLANNED portion — its serving count and the nutrition the
          plan carries for it (meal.nutrition, server-computed for exactly
          that portion). The base recipe's per-serving facts are labelled
          separately below, never a second unlabeled total. */}
      <div
        data-testid="planned-portion"
        className="flex flex-col gap-1 rounded-bb-lg border border-line bg-paper p-4"
      >
        <p className="text-xs font-medium uppercase tracking-wide text-ink-2">
          {t("views.recipe.plannedPortionLabel")} · {servingsLabel(meal.servings, t, locale)}
        </p>
        <p className="font-display text-2xl font-bold leading-none text-ink">
          {formatKcal(meal.nutrition.kcal, locale)}
        </p>
        <p className="text-sm text-ink-2">
          {t("views.mealCard.macroLine", {
            p: Math.round(meal.nutrition.proteinG),
            c: Math.round(meal.nutrition.carbsG),
            f: Math.round(meal.nutrition.fatG),
          })}
        </p>
      </div>

      <dl className="grid grid-cols-2 gap-3 rounded-bb-lg border border-line bg-paper p-4 text-sm">
        <div>
          <dt className="text-xs uppercase text-ink-2">{t("views.recipe.activeTimeLabel")}</dt>
          <dd className="text-ink">{formatMinutes(meal.activeTimeMinutes, locale)}</dd>
        </div>
        <div>
          <dt className="text-xs uppercase text-ink-2">{t("views.recipe.totalTimeLabel")}</dt>
          <dd className="text-ink">{formatMinutes(meal.totalTimeMinutes, locale)}</dd>
        </div>
      </dl>

      {meal.batchComponentId ? (
        // ADR-060 (WS2): a batch instance's portion is shared with the whole
        // batch — the owner refuses scale_servings for it (MEAL_NOT_SCALABLE),
        // so the client never offers the control it cannot honor.
        <p
          data-testid="batch-portion-fixed"
          className="rounded-bb-lg border border-line bg-paper p-4 text-sm text-ink-2"
        >
          {t("views.recipe.batchPortionsFixed")}
        </p>
      ) : (
        <ServingsStepper
          state={portion}
          mealId={meal.id}
          title={title}
          onStep={handleStep}
          onApply={() => void handleApply()}
          onCancel={handleCancel}
          onReload={load}
        />
      )}

      <RecipeDetailSection meal={meal} preview={portion.preview?.portion ?? null} />

      <OutcomeSheet
        open={outcomeMeal !== null}
        onOpenChange={open => {
          if (!open) {
            setOutcomeMeal(null)
            // Re-read the ledger so a just-recorded outcome shows on this page.
            loadLedger()
          }
        }}
        meal={outcomeMeal}
      />

      <SwapProposalSheet
        open={swapMeal !== null}
        onOpenChange={open => {
          if (!open) setSwapMeal(null)
        }}
        planId={planId}
        baseRevision={baseRevision}
        meal={swapMeal}
        intent="swap"
        onApplied={result => {
          // The persisted revision is the new truth: this meal keeps its id with a new dish, so the page re-seeds
          // its portion state from the plan and the recipe section loads the new recipe; the other screens refetch.
          setPlan(result.plan)
          const swapped = result.plan.meals.find(m => m.id === currentMeal.id)
          if (swapped) setPortion(initialPortionPreview(swapped.servings))
          notifyAmyDataChanged()
        }}
        onStaleRevision={() => {
          setSwapMeal(null)
          load()
        }}
      />
    </section>
  )
}

type RecipeLoadState =
  { status: "loading" } | { status: "success"; recipe: RecipeDetailResponse } | { status: "error" }

/**
 * The full sourced recipe view (S-5): ingredients with quantities, allergen
 * entries with relation + user-declared-allergen highlighting, steps,
 * per-serving nutrition when present, and a provenance chip. Falls back to
 * the honest RecipeGapPanels when the detail endpoint call fails — never a
 * placeholder ingredient/allergen/step list (AGENTS.md). The allergen
 * responsibility note always renders regardless of load state: it's static
 * safety guidance, not data from this endpoint.
 */
function RecipeDetailSection({
  meal,
  preview,
}: {
  meal: MealInstance
  /** The portion-preview's server-scaled portion while a preview is shown; null = the planned portion. */
  preview: NonNullable<RecipeDetailResponse["portion"]> | null
}) {
  const api = useApiClient()
  const { t, locale } = useI18n()
  const [state, setState] = useState<RecipeLoadState>({ status: "loading" })
  const [userAllergens, setUserAllergens] = useState<Set<string>>(new Set())
  // ADR-080: an active lactose intolerance flags milk-containing ingredients too (its own wording, never "allergen")
  const [lactoseLevel, setLactoseLevel] = useState<LactoseLevel | null>(null)

  useEffect(() => {
    let cancelled = false
    setState({ status: "loading" })
    // Hard constraints are fetched alongside the recipe purely for
    // highlighting — a failure there must never block the recipe itself
    // from rendering, so it's tracked independently via allSettled.
    // Portion truth: ask the SERVER for the recipe scaled to the planned
    // portion (`?servings=`), so ingredient quantities and nutrition describe
    // the same portion as the header. An unsupported serving count (legacy
    // plans) yields the base recipe, rendered with its own labels.
    Promise.allSettled([
      api.getRecipe(meal.recipeId, { servings: meal.servings }),
      api.getHardConstraints(),
    ]).then(([recipeResult, constraintsResult]) => {
      if (cancelled) return
      if (constraintsResult.status === "fulfilled") {
        const hc: HardConstraintsResponse = constraintsResult.value
        const ids = hc.constraints
          .filter(c => c.type === "allergen" && c.status === "active")
          .map(c => c.subjectId)
        setUserAllergens(new Set(ids))
        setLactoseLevel(lactoseFlagLevel(hc.constraints))
      } else {
        setUserAllergens(new Set())
      }
      if (recipeResult.status === "fulfilled") {
        setState({ status: "success", recipe: recipeResult.value })
      } else {
        setState({ status: "error" })
      }
    })
    return () => {
      cancelled = true
    }
  }, [api, meal.recipeId, meal.servings])

  return (
    <>
      {state.status === "loading" ? <LoadingState /> : null}

      {state.status === "error" ? (
        <>
          <RecipeIngredientsPanel />
          <RecipeAllergenPanel />
          <RecipeStepsPanel />
        </>
      ) : null}

      {state.status === "success" ? (
        <RecipeDetailPanels
          recipe={state.recipe}
          preview={preview}
          userAllergens={userAllergens}
          lactoseLevel={lactoseLevel}
          t={t}
          locale={locale}
        />
      ) : null}

      {/* Always rendered — legal/safety disclosure, not gated on load state. */}
      <div className="rounded-bb-md border border-line bg-cream p-3 text-xs text-ink-2">
        {t("views.recipe.allergenResponsibility")}
      </div>
    </>
  )
}

function RecipeDetailPanels({
  recipe,
  preview,
  userAllergens,
  lactoseLevel,
  t,
  locale,
}: {
  recipe: RecipeDetailResponse
  preview: NonNullable<RecipeDetailResponse["portion"]> | null
  userAllergens: Set<string>
  lactoseLevel: LactoseLevel | null
  t: TFn
  locale: Locale
}) {
  // Portion truth: with a server-scaled `portion` the ingredient list IS
  // the user's portion (quantities they cook/eat); without one (no planned
  // portion requested) it is the base recipe, labelled as such. The base
  // per-serving facts are always explicitly labelled and secondary.
  // While a portion PREVIEW is shown (ADR-055) the list switches to the
  // preview's server-scaled quantities under an explicit "(preview)"
  // heading for the selected serving — the same quantity the stepper and
  // the preview card describe — and back to the planned portion otherwise.
  const portion = recipe.portion ?? null
  const ingredientsTitle = preview
    ? preview.servings === 1
      ? t("views.recipe.previewIngredientsTitleOne")
      : t("views.recipe.previewIngredientsTitle", {
          servings: formatServings(preview.servings, locale),
        })
    : portion
      ? portion.servings === 1
        ? t("views.recipe.ingredientsForPortionOne")
        : t("views.recipe.ingredientsForPortion", {
            servings: formatServings(portion.servings, locale),
          })
      : t("views.recipe.ingredientsTitle")
  const ingredientLines = preview
    ? preview.ingredients
    : portion
      ? portion.ingredients
      : recipe.ingredients

  return (
    <>
      {recipe.description[locale] ? (
        <p className="text-sm text-ink-2">{recipe.description[locale]}</p>
      ) : null}

      <section
        aria-labelledby="recipe-ingredients-heading"
        className="rounded-bb-lg border border-line bg-paper p-4"
      >
        <h2 id="recipe-ingredients-heading" className="font-semibold text-ink">
          {ingredientsTitle}
        </h2>
        <ul className="mt-2 flex flex-col gap-1.5">
          {ingredientLines.map(line => (
            <li key={line.ingredientId} className="flex items-center justify-between gap-3 text-sm">
              <span className="text-ink">{line.displayName[locale]}</span>
              <span className="text-ink-2">{ingredientQuantityText(line, locale, t)}</span>
            </li>
          ))}
        </ul>

        <div
          data-testid="base-serving-facts"
          className="mt-3 flex flex-col gap-0.5 border-t border-line pt-3 text-xs text-ink-2"
        >
          {/* Null nutrition = incomplete composition (fail-closed): no number, no guess. */}
          {recipe.nutritionPerServing ? (
            <p className="flex flex-wrap items-baseline gap-x-2">
              <span className="font-medium">{t("views.recipe.baseServingLabel")}</span>
              <span>
                {formatKcal(recipe.nutritionPerServing.kcal, locale)} ·{" "}
                {t("views.mealCard.macroLine", {
                  p: Math.round(recipe.nutritionPerServing.proteinG),
                  c: Math.round(recipe.nutritionPerServing.carbsG),
                  f: Math.round(recipe.nutritionPerServing.fatG),
                })}
              </span>
            </p>
          ) : null}
          <p>{t("views.recipe.recipeMakes", { count: recipe.servingsDefault })}</p>
        </div>
      </section>

      <section
        aria-labelledby="recipe-allergens-heading"
        className="rounded-bb-lg border border-line bg-paper p-4"
      >
        <h2 id="recipe-allergens-heading" className="font-semibold text-ink">
          {t("views.recipe.allergensTitle")}
        </h2>
        {recipe.allergens.length === 0 ? (
          <p className="mt-1 text-sm text-ink-2">{t("views.recipe.allergensGap")}</p>
        ) : (
          <ul className="mt-2 flex flex-wrap gap-2">
            {recipe.allergens.map(entry => {
              const declaredAllergy = userAllergens.has(entry.allergen)
              // ADR-080: at "lactose-free dairy is fine" a served meal's dairy IS lactose-free — a
              // neutral note, never the alarm tone; "avoids all dairy" and allergies keep the alarm.
              const intoleranceFlag =
                !declaredAllergy && entry.allergen === "milk" ? lactoseLevel : null
              const flagged = declaredAllergy || intoleranceFlag === "avoid_all_dairy"
              const tone = flagged
                ? "border-destructive bg-destructive/15 text-destructive font-semibold"
                : intoleranceFlag === "lactose_free_ok"
                  ? "border-line bg-cream text-ink-2"
                  : entry.relation === "contains"
                    ? "border-danger/60 bg-coral/15 text-ink"
                    : entry.relation === "may_contain"
                      ? "border-gold/60 bg-gold/15 text-ink"
                      : "border-line bg-cream text-ink-2"
              return (
                <li
                  key={entry.allergen}
                  className={`flex items-center gap-1.5 rounded-bb-full border px-2.5 py-1 text-xs ${tone}`}
                >
                  <span>
                    {humanizeEnum(entry.allergen)} · {allergenRelationLabel(entry.relation, locale)}
                  </span>
                  {declaredAllergy ? <span>— {t("views.recipe.yourAllergenFlag")}</span> : null}
                  {intoleranceFlag === "avoid_all_dairy" ? (
                    <span>— {t("views.recipe.yourIntoleranceFlagAvoidAll")}</span>
                  ) : null}
                  {intoleranceFlag === "lactose_free_ok" ? (
                    <span>— {t("views.recipe.yourIntoleranceFlagFreeOk")}</span>
                  ) : null}
                </li>
              )
            })}
          </ul>
        )}
      </section>

      <section
        aria-labelledby="recipe-steps-heading"
        className="rounded-bb-lg border border-line bg-paper p-4"
      >
        <h2 id="recipe-steps-heading" className="font-semibold text-ink">
          {t("views.recipe.stepsTitle")}
        </h2>
        {recipe.steps[locale].length === 0 ? (
          <p className="mt-1 text-sm text-ink-2">{t("views.recipe.stepsGap")}</p>
        ) : (
          <ol className="mt-2 flex flex-col gap-2">
            {recipe.steps[locale].map((step, i) => (
              <li key={i} className="flex items-start gap-3 text-sm">
                <span className="flex size-6 shrink-0 items-center justify-center rounded-bb-full bg-green text-xs font-bold text-paper">
                  {i + 1}
                </span>
                <span className="mt-0.5 text-ink">{step}</span>
              </li>
            ))}
          </ol>
        )}
      </section>

      {/* Provenance disclosure: server-provided source note + origin, never a per-recipe confidence claim. */}
      <section className="rounded-bb-lg border border-line bg-paper p-4">
        <span className="inline-flex w-fit items-center rounded-bb-full border border-line bg-cream px-2 py-0.5 text-xs text-ink-2">
          {provenanceLabel(recipe.provenance, t)}
        </span>
        <p className="mt-2 text-sm text-ink-2">{recipe.sourceNote}</p>
      </section>
    </>
  )
}
